import React, { useState, type FC } from 'react';

import { useField } from 'formik';

import { Box } from '@clutter/clean';

import { Button, Table } from '@shared/components/bootstrap';
import { useToggle } from '@shared/hooks/use_toggle';

import {
  useConfirmation,
  Dialog as ConfirmationDialog,
  Response as ConfirmationResponse,
} from '@admin/components/confirmation';
import { Panel } from '@admin/components/helpers/panel';
import type { Landing__Location__ReviewInput } from '@admin/schema';

import { LandingLocationReviewModal } from './modal';
import { LandingLocationReviewRating } from './rating';

const LandingLocationReviewDeleteButton: FC<{
  review: Landing__Location__ReviewInput;
  onDelete(): void;
}> = ({ review, onDelete }) => {
  const { confirmation, confirm } = useConfirmation();
  const onClick = async () => {
    const confirmed = await confirm({
      title: <>Are you sure you want to delete {review.author}?</>,
      description: <>Deleting is permanent.</>,
    });
    if (confirmed !== ConfirmationResponse.Confirm) return;
    onDelete();
  };
  return (
    <>
      {confirmation && <ConfirmationDialog confirmation={confirmation} />}
      <Button kind="danger" onClick={onClick}>
        Delete
      </Button>
    </>
  );
};

const LandingLocationReviewEditButton: FC<{
  review: Landing__Location__ReviewInput;
  onSave(review: Landing__Location__ReviewInput): void;
}> = ({ review, onSave }) => {
  const { visible, onToggle } = useToggle();

  return (
    <>
      <Button kind="primary" onClick={() => onToggle()}>
        Edit
      </Button>
      {visible && (
        <LandingLocationReviewModal
          onSave={(input) => {
            onSave(input);
            onToggle();
          }}
          onClose={() => onToggle()}
          review={review}
        />
      )}
    </>
  );
};

const LandingLocationReviewNewButton: FC<{
  onSave(review: Landing__Location__ReviewInput): void;
}> = ({ onSave }) => {
  const { visible, onToggle } = useToggle();

  return (
    <>
      <Button kind="primary" onClick={() => onToggle()}>
        Add
      </Button>
      {visible && (
        <LandingLocationReviewModal
          onSave={(input) => {
            onSave(input);
            onToggle();
          }}
          onClose={() => onToggle()}
        />
      )}
    </>
  );
};

export const LandingLocationReviewsPanel: FC = () => {
  const [field, _, helpers] = useField<Array<Landing__Location__ReviewInput>>('reviews');

  return (
    <Panel>
      <Panel.Header>
        <Panel.Title>Reviews</Panel.Title>
      </Panel.Header>
      <Panel.Body>
        <Table striped>
          <thead>
            <tr>
              <th className="text-left">Author</th>
              <th className="text-left">Message</th>
              <th className="text-left">Rating</th>
              <th className="col-zero" />
            </tr>
          </thead>
          <tbody>
            {field.value.map(
              (review, index) =>
                !review._destroy && (
                  <tr key={index}>
                    <td className="text-left">{review.author}</td>
                    <td className="text-left">{review.message}</td>
                    <td className="text-left">
                      <LandingLocationReviewRating review={review} />
                    </td>
                    <td className="col-zero">
                      <Box.Flex gap="4px">
                        <LandingLocationReviewEditButton
                          review={review}
                          onSave={(input) => {
                            const reviews = [...field.value];
                            reviews[index] = {
                              ...reviews[index],
                              ...input,
                              rating: Number(input.rating),
                            };
                            helpers.setValue(reviews);
                          }}
                        />
                        <LandingLocationReviewDeleteButton
                          review={review}
                          onDelete={() => {
                            const reviews = [...field.value];
                            reviews[index] = {
                              ...reviews[index],
                              _destroy: !reviews[index]._destroy,
                            };
                            helpers.setValue(reviews);
                          }}
                        />
                      </Box.Flex>
                    </td>
                  </tr>
                ),
            )}
          </tbody>
        </Table>
      </Panel.Body>
      <Panel.Footer align="right">
        <LandingLocationReviewNewButton
          onSave={(review) => {
            const reviews = [...field.value];
            reviews.push({
              ...review,
              rating: Number(review.rating),
            });
            helpers.setValue(reviews);
          }}
        />
      </Panel.Footer>
    </Panel>
  );
};
